import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Breadcrumb from '@/components/common/Breadcrumb';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import CardBody from '@/components/ui/cards/CardBody';
import { Input } from '@/components/ui/inputs/input';
import ToastMe from '@/components/ui/ToastMe';
import validationErrors from '@/lib/validationErrors';
import { updateAdminPasswordService } from '@/services/AdminServices';

const ChangePasswordPage = ({ pageTitle = 'Change Password' }) => {
  const dispatch = useDispatch();
  const { control, handleSubmit, getValues, reset, formState: { errors, isSubmitting } } = useForm();

  const onSubmit = async (data) => {
    // client-side checks
    if (data.old_password === data.password) {
      ToastMe('New password must differ from old password', 'error');
      return;
    }
    if (data.confirm_password !== data.password) {
      ToastMe('Password and confirmation do not match', 'error');
      return;
    }

    try {
      // calls PUT /admin/profile/change-password with { old_password, password }
      await dispatch(updateAdminPasswordService({
        old_password: data.old_password,
        password:     data.password,
      }));
      ToastMe('Password updated successfully', 'success');
      reset();
    } catch (err) {
      const { message, errorData } = err;
      validationErrors(errorData, message);
    }
  };

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />
      <Card>
        <CardHeader isBorder>
          <h3 className="db-card-title">{pageTitle}</h3>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Controller
                name="old_password"
                control={control}
                rules={{ required: true, minLength: 6, maxLength: 20 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    labelText="Old Password"
                    labelStyleClassName="db-field-title required"
                    errorType={errors.old_password?.type}
                    errorData={{ minLength: '6', maxLength: '20' }}
                    autoComplete="current-password"
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{ required: true, minLength: 6, maxLength: 20 }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    labelText="New Password"
                    labelStyleClassName="db-field-title required"
                    errorType={errors.password?.type}
                    errorData={{ minLength: '6', maxLength: '20' }}
                    autoComplete="new-password"
                  />
                )}
              />

              <Controller
                name="confirm_password"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => value === getValues('password') || 'Passwords do not match',
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="password"
                    labelText="Confirm Password"
                    labelStyleClassName="db-field-title required"
                    errorType={errors.confirm_password?.type}
                    autoComplete="new-password"
                  />
                )}
              />

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="db-btn bg-primary text-white"
                  disabled={isSubmitting}
                >
                  <i className="lab lab-save" />
                  <span>Save</span>
                  {isSubmitting && (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  );
};

export default ChangePasswordPage;
